import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LANGUAGES = [
  { code: "en", name: "English" }, { code: "es", name: "Spanish" }, { code: "fr", name: "French" },
  { code: "de", name: "German" }, { code: "it", name: "Italian" }, { code: "pt", name: "Portuguese" },
  { code: "nl", name: "Dutch" }, { code: "sv", name: "Swedish" }, { code: "no", name: "Norwegian" },
  { code: "da", name: "Danish" }, { code: "fi", name: "Finnish" }, { code: "pl", name: "Polish" },
  { code: "cs", name: "Czech" }, { code: "sk", name: "Slovak" }, { code: "hu", name: "Hungarian" },
  { code: "ro", name: "Romanian" }, { code: "bg", name: "Bulgarian" }, { code: "el", name: "Greek" },
  { code: "ru", name: "Russian" }, { code: "uk", name: "Ukrainian" }, { code: "tr", name: "Turkish" },
  { code: "ar", name: "Arabic" }, { code: "he", name: "Hebrew" }, { code: "fa", name: "Persian" },
  { code: "hi", name: "Hindi" }, { code: "bn", name: "Bengali" }, { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" }, { code: "mr", name: "Marathi" }, { code: "gu", name: "Gujarati" },
  { code: "kn", name: "Kannada" }, { code: "ml", name: "Malayalam" }, { code: "pa", name: "Punjabi" },
  { code: "ur", name: "Urdu" }, { code: "ne", name: "Nepali" }, { code: "si", name: "Sinhala" },
  { code: "th", name: "Thai" }, { code: "vi", name: "Vietnamese" }, { code: "id", name: "Indonesian" },
  { code: "ms", name: "Malay" }, { code: "tl", name: "Filipino" }, { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" }, { code: "ko", name: "Korean" }, { code: "sw", name: "Swahili" },
  { code: "am", name: "Amharic" }, { code: "yo", name: "Yoruba" }, { code: "zu", name: "Zulu" },
];

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("en");
  const [, setVersion] = useState(0);
  const cacheRef = useRef({});
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);

  const langName = LANGUAGES.find((l) => l.code === lang)?.name || "English";

  const getTranslation = useCallback(
    (text) => cacheRef.current[lang]?.[text],
    [lang]
  );

  const flush = useCallback(async (activeLang, activeName) => {
    const texts = Array.from(pendingRef.current).slice(0, 60);
    pendingRef.current = new Set();
    if (!texts.length) return;
    try {
      const res = await axios.post(`${API}/translate`, { texts, language: activeName });
      if (!cacheRef.current[activeLang]) cacheRef.current[activeLang] = {};
      texts.forEach((t, i) => {
        cacheRef.current[activeLang][t] = res.data.translations[i] || t;
      });
      setVersion((v) => v + 1);
    } catch {}
  }, []);

  const requestTranslation = useCallback(
    (text) => {
      if (lang === "en" || !text) return;
      if (cacheRef.current[lang]?.[text]) return;
      pendingRef.current.add(text);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => flush(lang, langName), 400);
    },
    [lang, langName, flush]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, langName, getTranslation, requestTranslation }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export const T = ({ children }) => {
  const { lang, getTranslation, requestTranslation } = useLanguage();
  const text = typeof children === "string" ? children : String(children ?? "");
  const translated = getTranslation(text);
  React.useEffect(() => {
    if (lang !== "en" && !translated) requestTranslation(text);
  }, [text, lang, translated, requestTranslation]);
  if (lang === "en") return <>{text}</>;
  return <>{translated || text}</>;
};
